import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card, Chip, Stack, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Box, IconButton, Tooltip, useMediaQuery, useTheme, Skeleton,
} from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { AvisoDeLeitura } from "../components/AvisoDeLeitura";
import { useTenant } from "../context/TenantContext";
import { useAccess } from "../context/AccessContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";

interface Report {
  id: string;
  date: string;
  type: string;
  template?: string;
  sent_ok: number;
  sent_total: number;
  sent_label: string;
  status: "ok" | "partial" | "error" | "none";
  last_sent_at?: string;
  first_sent_at?: string;
}

interface ReportsResponse {
  tenant: string;
  reports: Report[];
  stale: boolean;
  error?: string;
}

const STATUS: Record<string, { label: string; color: "success" | "warning" | "error" | "default"; Icon: any }> = {
  ok:      { label: "Enviado",   color: "success", Icon: CheckCircleRoundedIcon },
  partial: { label: "Parcial",   color: "warning", Icon: WarningAmberRoundedIcon },
  error:   { label: "Falha",     color: "error",   Icon: ErrorRoundedIcon },
  none:    { label: "Não env.",  color: "default", Icon: BlockRoundedIcon },
};

function friendlyDate(v?: string): string {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return v; }
}

export function Reports() {
  const { tenant } = useTenant();
  const { can } = useAccess();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const api = useApi();
  const [filter, setFilter] = useState<"todos" | Report["status"]>("todos");

  const q = useQuery({
    queryKey: ["reports", tenant],
    queryFn: () => api.call<ReportsResponse>("/api/reports?limit=50"),
    enabled: !!tenant,
    staleTime: 30_000,
  });

  const all = q.data?.reports ?? [];
  const items = all.filter((r) => filter === "todos" || r.status === filter);

  return (
    <>
      <PageHeader
        eyebrow="Central"
        title="Relatórios"
        subtitle={<>Enviados nos últimos 60 dias · <strong>{tenant ?? "?"}</strong></>}
        action={can("relatorios.reenviar") ? (
          <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
            component={RouterLink} to="/relatorios/agendar" data-testid="btn-schedule">
            Novo envio
          </Button>
        ) : undefined}
      />

      {q.data?.stale && <AvisoDeLeitura erro={q.data.error} oQue="os relatórios" />}

      <Stack direction="row" gap={1} flexWrap="wrap">
        {[
          { id: "todos", label: `Todos${all.length ? ` (${all.length})` : ""}` },
          { id: "ok", label: "Enviados" },
          { id: "partial", label: "Parciais" },
          { id: "error", label: "Falhas" },
          { id: "none", label: "Não enviados" },
        ].map((f) => (
          <Chip key={f.id} label={f.label} onClick={() => setFilter(f.id as any)}
            color={filter === f.id ? "primary" : "default"}
            variant={filter === f.id ? "filled" : "outlined"}
            data-testid={`report-filter-${f.id}`} />
        ))}
      </Stack>

      <Card variant="outlined" sx={{ overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              {!isSm && <TableCell>Tipo / Template</TableCell>}
              <TableCell align="right">Envio</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {q.isLoading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={90} /></TableCell>
                {!isSm && <TableCell><Skeleton width={200} /></TableCell>}
                <TableCell align="right"><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={70} /></TableCell>
                <TableCell />
              </TableRow>
            ))}
            {!q.isLoading && items.map((r) => {
              const S = STATUS[r.status] ?? STATUS.none;
              return (
                <TableRow key={`${r.date}-${r.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{friendlyDate(r.last_sent_at) || r.date}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                      {r.id?.slice(0, 24)}
                    </Typography>
                  </TableCell>
                  {!isSm && (
                    <TableCell>
                      <Typography variant="body2">{r.type}</Typography>
                      {r.template && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{r.template}</Typography>}
                    </TableCell>
                  )}
                  <TableCell align="right"><strong>{r.sent_label}</strong></TableCell>
                  <TableCell>
                    <Chip
                      icon={<S.Icon sx={{ fontSize: "16px !important" }} />}
                      label={S.label} size="small" color={S.color as any} variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalhe">
                      <IconButton size="small" component={RouterLink} to={`/relatorios/${encodeURIComponent(r.id)}`}>
                        <LaunchRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!q.isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                    Nenhum relatório com este filtro.
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
